import React, { createContext, useContext, useMemo } from 'react';
import { serverTimestamp } from 'firebase/firestore';
import { deleteDoc, setDoc, writeBatch } from './writes';
import { docRef, COLLECTIONS } from './paths';
import { db } from '../lib/firebase';
import { useAuth } from '../context/AuthContext';
import { messageWrites, isPending, sendAgainDocument } from '../domain/notifications';
import { useCollectionWindow } from './useCollectionWindow';

// The outbox.
//
// Same shape as invoiceRequests: the client appends, a Cloud Function consumes.
// `deliverMessage` in functions/ drains this queue — renders the body from the
// shared domain code, hands it to a transport, and writes back the outcome,
// which the client is not allowed to write itself.
//
// Written with writeBatch rather than a plain set: a receipt is queued in the
// same breath as the payment that earns it, and a batch commits offline while a
// transaction does not. A teacher taking a payment in a studio with no signal
// gets both, or neither.

const NotificationsContext = createContext(null);
const TRACK_LIMIT = 2000;

export function NotificationsProvider({ children }) {
  const { user } = useAuth();

  // The outbox window, from whichever read path serves this session (#207 U5).
  // `withMetadata` keeps the snapshot path's `includeMetadataChanges`.
  const read = useCollectionWindow({
    uid: user?.uid,
    collection: COLLECTIONS.messages,
    limit: TRACK_LIMIT,
    withMetadata: true,
  });
  const messages = read.docs;
  const loading = read.loading;

  const value = useMemo(() => {
    // Queue a batch of messages. Each needs `kind`, `channel`, `subject` (what
    // it is about, which becomes its id) and at least one recipient.
    // The batch `queue` would commit, uncommitted, and the ids it writes —
    // or null when there is nothing to write (#590). For a caller that needs
    // the messages to ride on another batch rather than go on their own: an
    // attendance send button hands this to `setAttendance` as its follow-on,
    // so the message is as durable as the attendance from the click, and is
    // sent only once the attendance is acknowledged. Add Transaction's receipt
    // rides the payment the same way.
    const messageBatch = (requests = []) => {
      if (!user) return null;
      const writes = messageWrites(requests, messages);
      if (writes.length === 0) return null;
      const batch = writeBatch(db);
      for (const { id, message } of writes) {
        batch.set(
          docRef(user.uid, COLLECTIONS.messages, id),
          { ...message, requestedAt: serverTimestamp() },
          { merge: true },
        );
      }
      return { batch, ids: writes.map((w) => w.id) };
    };

    const queue = async (requests = []) => {
      if (!user) return [];

      // Messages the sender has already taken. Not ours to rewrite.
      //
      // The id is derived from what a message is about, so asking twice is
      // ordinary. While the message is still `queued` the second ask is a
      // re-addressing and lands on the same document (the merge below). Once
      // the sender has it, the outbox rules refuse the write: a client that
      // could set `sent` back to `queued` could have deliverMessage post a
      // family the same receipt as often as it liked.
      //
      // So the repeat is dropped here rather than attempted: a batch Firestore
      // rejects fails the whole commit, as "Missing or insufficient
      // permissions". `failed` is not in the taken set: it reached nobody, the
      // rules allow it back to `queued`, and asking again is the teacher's
      // retry.
      //
      // The id is derived from `message.channel` rather than the request's
      // own: `queuedMessage` folds an unrecognised channel to email, so the id
      // is derived from what will be stored.
      //
      // All three — addressed, the id, taken — are `messageWrites` in
      // domain/notifications.js.
      const writes = messageWrites(requests, messages);
      if (writes.length === 0) return [];

      const batch = writeBatch(db);
      const ids = [];
      for (const { id, message } of writes) {
        ids.push(id);
        batch.set(
          docRef(user.uid, COLLECTIONS.messages, id),
          { ...message, requestedAt: serverTimestamp() },
          // Merge so re-asking for the same message updates the recipients
          // rather than writing a second copy of it — the id already says the
          // two requests are the same intent.
          { merge: true },
        );
      }
      await batch.commit();
      return ids;
    };

    // Send again (#489): a failed message back to `queued`, merged, with
    // nothing else on it changed — the recipients, the context and the
    // sender's `delivery` record are what the rules hold an ask to, and
    // `redeliverMessage` is what picks it up. Whether a row offers it is the
    // core's (`canSendAgain`); the sender holds the same bound, so an ask this
    // build should not have offered is answered there rather than trusted.
    const sendAgain = async (id) => {
      if (!user || !id) return;
      await setDoc(docRef(user.uid, COLLECTIONS.messages, id), sendAgainDocument(serverTimestamp()), { merge: true });
    };

    // New Message (#522): a teacher's own email, queued like every other
    // message. `write` is `teacherMessageWrite`'s, already refused by the
    // composer if it would be by the rules; its id is a fresh one made when
    // the composer opened, so a second press of Send is the same document.
    // Merged for that reason, as `queue` merges.
    const sendTeacherMessage = async (write) => {
      if (!user || !write?.id) return null;
      const batch = writeBatch(db);
      batch.set(
        docRef(user.uid, COLLECTIONS.messages, write.id),
        { ...write.message, requestedAt: serverTimestamp() },
        { merge: true },
      );
      await batch.commit();
      return write.id;
    };

    // Cancel Scheduled Send (#606): a message sent later, withdrawn before it
    // goes, is deleted — which `firestore.rules` allows a client while it is
    // `queued` and nothing else. The sender takes a message in a transaction
    // that reads it first, so exactly one of the two wins: deleted first, the
    // sender finds it gone and sends nothing; taken first, the rules refuse
    // the delete, and the refusal reaches the sync status as any refused
    // write does. Whether a row offers it is the core's (`canWithdraw`).
    const withdraw = async (id) => {
      if (!user || !id) return;
      await deleteDoc(docRef(user.uid, COLLECTIONS.messages, id));
    };

    return {
      loading,
      // The read stopped before it answered (#647) — src/domain/reading.js.
      failed: read.failed,
      messages,
      queue,
      messageBatch,
      sendAgain,
      withdraw,
      sendTeacherMessage,
      // Including the ones the sender has claimed: a message in flight has not
      // arrived anywhere yet, and counting only `queued` would tick the total
      // down mid-send and back up if it failed.
      pending: messages.filter(isPending),
    };
  }, [messages, loading, read.failed, user]);

  return (
    <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotificationsContext);
  if (!ctx) throw new Error('useNotifications must be used within a NotificationsProvider');
  return ctx;
}
